import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  deleteOrder,
  previewDeleteOrder,
  OrderDeletionBlockedError,
  type OrderDeletionPreview,
  type OrderDeletionResult,
  type OrderDetail,
} from '../../lib/orders';
import { DELETE_REASONS, type DeleteReason } from '../../lib/order-edit-rules';

interface DeleteOrderDialogProps {
  order: OrderDetail;
  isOpen: boolean;
  onClose: () => void;
  onDeleted: (result: OrderDeletionResult) => void;
}

const DEFAULT_REASON: DeleteReason['code'] = DELETE_REASONS[0]?.code ?? 'Anders';

export function DeleteOrderDialog({ order, isOpen, onClose, onDeleted }: DeleteOrderDialogProps) {
  const [preview, setPreview] = useState<OrderDeletionPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reasonCode, setReasonCode] = useState<DeleteReason['code']>(DEFAULT_REASON);
  const [redenAnders, setRedenAnders] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setPreviewError(null);
      setReasonCode(DEFAULT_REASON);
      setRedenAnders('');
      setError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    previewDeleteOrder(order.id)
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch((err) => {
        if (!cancelled) {
          setPreviewError(err instanceof Error ? err.message : 'Preview mislukt');
        }
      })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, order.id]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!preview?.canDelete) return;
    if (reasonCode === 'Anders' && !redenAnders.trim()) {
      setError('Toelichting is verplicht bij "Anders"');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await deleteOrder(
        order.id,
        reasonCode,
        reasonCode === 'Anders' ? redenAnders.trim() : undefined
      );
      onDeleted(result);
      onClose();
    } catch (err) {
      if (err instanceof OrderDeletionBlockedError) {
        setPreview({
          canDelete: false,
          blockCode: err.blockCode,
          blockReason: err.message,
          aantalWasserijItems: preview?.aantalWasserijItems ?? 0,
          aantalRitten: preview?.aantalRitten ?? 0,
          werkOrderNummer: preview?.werkOrderNummer ?? order.orderNumber,
          huidigeStatus: preview?.huidigeStatus ?? order.status,
          waarschuwingen: preview?.waarschuwingen ?? [],
        });
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Verwijderen mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">WO verwijderen</h2>
          <p className="text-xs text-slate-500 mt-1">Ordernummer: {order.orderNumber}</p>
        </div>

        <div className="px-6 py-4 space-y-3">
          {previewLoading && (
            <p className="text-sm text-slate-500">Impact wordt opgehaald…</p>
          )}

          {previewError && (
            <p className="text-xs text-red-600">{previewError}</p>
          )}

          {preview && !preview.canDelete && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              <strong>Verwijderen niet mogelijk</strong>
              <p className="mt-1">{preview.blockReason || 'Onbekende blokkade'}</p>
              <p className="mt-2 text-red-600">
                Tip: gebruik <em>Annuleren</em> in plaats van verwijderen wanneer de
                order al een operationele status heeft.
              </p>
            </div>
          )}

          {preview && preview.canDelete && (
            <>
              <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2">
                <strong>Wordt verwijderd:</strong>
                <ul className="mt-1 list-disc list-inside text-slate-600">
                  <li>1 Werk Order ({preview.werkOrderNummer || order.orderNumber})</li>
                  <li>{preview.aantalWasserijItems} wasserij-item{preview.aantalWasserijItems === 1 ? '' : 's'}</li>
                  <li>{preview.aantalRitten} geplande rit{preview.aantalRitten === 1 ? '' : 'ten'}</li>
                </ul>
              </div>

              {preview.waarschuwingen.length > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <strong>Blijft achter buiten Salesforce:</strong>
                  <ul className="mt-1 list-disc list-inside text-amber-700 space-y-1">
                    {preview.waarschuwingen.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              <label className="block">
                <span className="text-sm text-slate-700">Reden</span>
                <select
                  value={reasonCode}
                  onChange={(e) => setReasonCode(e.target.value as DeleteReason['code'])}
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                  disabled={submitting}
                >
                  {DELETE_REASONS.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>

              {reasonCode === 'Anders' && (
                <label className="block">
                  <span className="text-sm text-slate-700">Toelichting</span>
                  <textarea
                    value={redenAnders}
                    onChange={(e) => setRedenAnders(e.target.value)}
                    rows={3}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                    placeholder="Waarom wordt deze WO verwijderd?"
                    disabled={submitting}
                    maxLength={255}
                  />
                </label>
              )}

              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                <strong>Permanent.</strong> Salesforce Recycle Bin houdt de records
                15 dagen — daarna is herstel niet meer mogelijk. De actie wordt
                vastgelegd in <em>Werk Order Verwijderingen</em>.
              </div>
            </>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          {preview?.canDelete && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                submitting ||
                (reasonCode === 'Anders' && !redenAnders.trim())
              }
              className={clsx(
                'text-sm font-medium px-3 py-1.5 rounded',
                submitting
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              )}
            >
              {submitting ? 'Verwijderen…' : 'WO verwijderen'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
